import { InputError } from "@op/opds.opux"
import { useFormValidationAlert } from "@op/luna.yaweb-common-js/FormValidationAlert"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useEffect } from "react"
import { Message } from "@messageformat/react"

/**
 * Custom validation error which uses opux InputError internally.
 *
 * Uses useFormValidationAlert hook to check the status of given errorId to show/hide error component
 *
 * Error is shown in following cases:
 * - when there is an error for given id AND requireVisible is not given in props
 * - when there is an error for given id AND requireVisible is true AND isVisible is set true in hook
 *
 * In other cases, returns null.
 */
const ValidationError = ({ errorId, errorMessageId, requireVisible, className, onError, ...props }) => {
  const { errors, isVisible } = useFormValidationAlert(errorId)
  const [error] = errors
  const isErrorVisible = error && (!requireVisible || isVisible)

  useEffect(() => {
    if (isErrorVisible && onError) {
      onError()
    }
  }, [isErrorVisible, onError])

  if (!isErrorVisible) {
    return null
  }

  return (
    <InputError
      error={error.fieldMessage || <Message id={errorMessageId} />}
      className={classNames("validation-error", className)}
      {...props}
      aria-live="assertive"
    />
  )
}

ValidationError.displayName = "ValidationError"
ValidationError.propTypes = {
  /**
   * errorId is used to get specific error status from useFormValidationAlert.
   * Without errorId, all errors are returned.
   */
  errorId: PropTypes.string.isRequired,

  /**
   * If errors are displayed always or waiting until isVisible is on in the hook
   */
  requireVisible: PropTypes.bool
}

export default ValidationError
